import { data, Link, useLoaderData } from 'react-router'
import { type RouteAnalyticsHandle } from '#app/analytics/constants.ts'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Separator } from '#app/components/ui/separator.tsx'
import {
	categoryHref,
	getPublishablePages,
} from '#app/scoring/publishable-pages.server.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PAGE_MEASURE, PAGE_PROSE } from '#app/utils/layout.ts'
import { type Route } from './+types/$citySlug.ts'

export async function loader({ params }: Route.LoaderArgs) {
	const { citySlug } = params

	const city = await prisma.city.findUnique({
		where: {
			slug: citySlug,
			businesses: { some: { status: 'published' } },
		},
		select: {
			id: true,
			name: true,
			slug: true,
		},
	})

	if (!city) {
		throw new Response('City not found', { status: 404 })
	}

	// Get categories with business counts for this city
	const categoriesWithCounts = await prisma.category.findMany({
		where: {
			businesses: {
				some: {
					status: 'published',
					cities: { some: { id: city.id } },
				},
			},
		},
		select: {
			id: true,
			name: true,
			slug: true,
			_count: {
				select: {
					businesses: {
						where: {
							status: 'published',
							cities: { some: { id: city.id } },
						},
					},
				},
			},
		},
		orderBy: { name: 'asc' },
	})

	// Get featured businesses in this city
	const featuredBusinesses = await prisma.business.findMany({
		where: {
			status: 'published',
			cities: { some: { id: city.id } },
			isFeatured: true,
		},
		select: {
			id: true,
			name: true,
			slug: true,
			categories: {
				select: { id: true, name: true, slug: true },
				take: 1,
			},
		},
		take: 3,
	})

	// Get total business count
	const totalBusinesses = await prisma.business.count({
		where: {
			status: 'published',
			cities: { some: { id: city.id } },
		},
	})

	// Get other cities for navigation
	const otherCities = await prisma.city.findMany({
		where: {
			businesses: { some: { status: 'published' } },
			NOT: { id: city.id },
		},
		select: {
			id: true,
			name: true,
			slug: true,
		},
		orderBy: { name: 'asc' },
		take: 8,
	})

	/*
	 * Where each category tile in this city should point.
	 *
	 * The local ranking only exists where the pair clears both floors; below
	 * that /{city}/{category} 301s. Linking every tile at the pair would send
	 * most of this page — and at 56 cities by 30 categories, a large share of
	 * the site's internal links — into a redirect. Unpublishable pairs fall
	 * back to the category's own destination, which is a real page.
	 */
	const { cityCategories, bestCategories } = await getPublishablePages()
	const rankedCategorySlugs = new Set(bestCategories.map((c) => c.slug))
	const publishablePairs = new Set(
		cityCategories
			.filter((pair) => pair.citySlug === city.slug)
			.map((pair) => pair.categorySlug),
	)

	return data({
		city,
		categories: categoriesWithCounts.map((category) => ({
			...category,
			localRanking: publishablePairs.has(category.slug),
			href: publishablePairs.has(category.slug)
				? `/${city.slug}/${category.slug}`
				: categoryHref(category.slug, rankedCategorySlugs),
		})),
		featuredBusinesses,
		totalBusinesses,
		otherCities,
	})
}

export const handle = {
	// Read by useRouteAnalytics in root.tsx; tags every pageview and
	// client event fired on this route with its page archetype.
	analytics: { pageType: 'city_hub' } satisfies RouteAnalyticsHandle,
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		const city = data?.city
		return city ? { name: city.name, url: `/cities/${city.slug}` } : null
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const city = loaderData?.city
		if (!city) return null
		const canonical = `${origin}/cities/${city.slug}`

		const structuredData =
			loaderData.categories.length > 0
				? [
						buildItemListLd({
							name: `Professional Services in ${city.name}`,
							items: loaderData.categories.map((cat) => ({
								name: cat.name,
								url: `${origin}/${city.slug}/${cat.slug}`,
							})),
						}),
					]
				: undefined

		// Counts, not adjectives. "Honest guidance" and "vetted
		// recommendations" describe a service we do not provide, and they were
		// the whole of what these three descriptions said.
		const title = `Businesses in ${city.name}, TX | DFW Professionals`
		const description = `${loaderData.totalBusinesses.toLocaleString()} businesses serving ${city.name} across ${loaderData.categories.length} categories, scored against a published rubric on what reviewers wrote. Sponsored placements are labeled and excluded.`

		return {
			title,
			description,
			canonical,
			openGraph: {
				title,
				description,
				type: 'website',
				url: canonical,
				image: `${origin}/og/city/${city.slug}.png`,
			},
			twitter: {
				card: 'summary_large_image',
				title,
				description,
				image: `${origin}/og/city/${city.slug}.png`,
			},
			structuredData,
		}
	},
}

export default function CityHubPage() {
	const { city, categories, featuredBusinesses, totalBusinesses, otherCities } =
		useLoaderData<typeof loader>()

	return (
		<>
			{/* ═══════════════════════════════════════════════════════════════════
			    HERO SECTION — City-Specific Editorial Guide
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="pt-16 pb-12 md:pt-24 md:pb-16">
				<div className={PAGE_MEASURE}>
					{/* Breadcrumb context */}
					<div className="text-muted-foreground mb-6 flex items-center gap-2 text-sm">
						<Link to="/cities" className="hover:text-foreground">
							All Cities
						</Link>
						<Icon name="chevron-right" className="size-4" />
						<span className="text-foreground">{city.name}</span>
					</div>

					<h1 className="mb-4 font-serif text-3xl tracking-tight md:text-4xl">
						Businesses in {city.name}
					</h1>

					{/*
					 * Says what this page holds. The previous line claimed {city.name}
					 * "has specific patterns in demand, availability, and the
					 * professionals who serve this area" — a measured-sounding claim
					 * about a market nobody measured, rendered identically for all 55
					 * cities with only the name swapped. Two whole sections below it
					 * did the same thing at greater length and were removed.
					 */}
					<p
						className={`text-muted-foreground mb-6 text-lg leading-relaxed ${PAGE_PROSE}`}
					>
						Every business here lists {city.name} as an area it serves. Within a
						category they are ordered by the same published rubric used across
						the site, applied to the public reviews each has already received.
					</p>

					{/* Quick stats */}
					<div className="flex flex-wrap gap-4 text-sm">
						<div className="flex items-center gap-2">
							<Icon name="check" className="size-4 text-green-600" />
							<span className="tabular-nums">
								{totalBusinesses.toLocaleString()} business
								{totalBusinesses !== 1 ? 'es' : ''} serving {city.name}
							</span>
						</div>
						<div className="flex items-center gap-2">
							<Icon name="view-grid" className="text-muted-foreground size-4" />
							<span className="tabular-nums">
								{categories.length}{' '}
								{categories.length === 1 ? 'category' : 'categories'}
							</span>
						</div>
					</div>
				</div>
			</section>

			<div className={PAGE_MEASURE}>
				<Separator />
			</div>

			{/* ═══════════════════════════════════════════════════════════════════
			    CATEGORIES WITH EDITORIAL ANGLES
			    ═══════════════════════════════════════════════════════════════════ */}
			{categories.length > 0 && (
				<section className="py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						<div className="mb-8">
							<h2 className="mb-3 font-serif text-2xl tracking-tight">
								Browse by category
							</h2>
							{/*
							 * Promised "{city}-specific context, recommendations and local
							 * FAQs" on every tile. The per-tile line below already says
							 * accurately where each link goes, which is the honest version
							 * of the same promise.
							 */}
							<p className="text-muted-foreground max-w-xl">
								Categories with enough scored businesses serving {city.name} are
								ranked for the city. The rest link to the metro-wide ranking.
							</p>
						</div>

						<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
							{categories.map((category) => (
								<Link key={category.id} to={category.href} className="group">
									<Card className="h-full transition-all hover:shadow-md">
										<CardContent className="p-5">
											<div className="mb-3 flex items-start justify-between">
												<h3 className="font-medium group-hover:underline">
													{category.name}
												</h3>
												<Icon
													name="arrow-right"
													className="text-muted-foreground size-4 shrink-0 transition-transform group-hover:translate-x-1"
												/>
											</div>
											<p className="text-muted-foreground mb-3 text-sm">
												{category._count.businesses} professional
												{category._count.businesses !== 1 ? 's' : ''} in{' '}
												{city.name}
											</p>
											{/*
											 * Says where the link actually goes. The tile promised
											 * "{city}-specific recommendations" for every category,
											 * including those with no local ranking, where it now
											 * leads to the metro page instead.
											 */}
											<p className="text-muted-foreground/70 text-xs">
												{category.localRanking
													? `Ranked for ${city.name} →`
													: 'View the metro ranking →'}
											</p>
										</CardContent>
									</Card>
								</Link>
							))}
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    CITY-SPECIFIC FAQ
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="bg-muted/30 border-y py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className={PAGE_PROSE}>
						<h2 className="mb-8 font-serif text-2xl tracking-tight">
							About this page
						</h2>

						{/*
						 * Was five questions of unmeasured market claims: appointment
						 * wait times, whether pricing differs by city, whether local
						 * businesses are preferable. One answer told the reader that
						 * "the key is talking to actual past clients, not just reading
						 * online reviews", on a page whose ordering is derived entirely
						 * from reading online reviews.
						 *
						 * These three are answerable from how the site works, and are the
						 * questions a reader actually needs settled before trusting an
						 * ordering they did not compute themselves.
						 */}
						<Accordion type="single" collapsible className="w-full">
							<AccordionItem value="what-serving-means">
								<AccordionTrigger>
									What does &quot;serving {city.name}&quot; mean here?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									That the business lists {city.name} among the areas it covers.
									It does not mean the business is based here, and we have not
									confirmed it will travel to a given address. Check directly
									before booking.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="ordering">
								<AccordionTrigger>
									Why is this order different from the metro-wide ranking?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									The rubric is identical; the field is not. A city page scores
									only the businesses that serve {city.name}, so a business
									ranked mid-table across the metro can lead here. See{' '}
									<Link
										className="underline underline-offset-4"
										to="/scoring-method"
									>
										how we score
									</Link>
									.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="credentials">
								<AccordionTrigger>
									Have you verified licences or insurance?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									No. We read public reviews with software. Nobody here inspects
									work, interviews customers or checks a licence, and a high
									score is not a credential. Verify licensing through the
									relevant Texas state board yourself before hiring.
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</div>
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    FEATURED BUSINESSES
			    ═══════════════════════════════════════════════════════════════════ */}
			{featuredBusinesses.length > 0 && (
				<section className="py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						{/*
						 * Says the selection is ours, not earned. Without that line it
						 * reads as an editorial shortlist, which is the exact impression
						 * a labeled slot exists to prevent.
						 */}
						<h2 className="mb-2 font-serif text-xl tracking-tight">
							Featured in {city.name}
						</h2>
						<p className="text-muted-foreground mb-6 text-sm">
							Chosen by us, not by score. Ranked lists elsewhere on the site are
							unaffected.
						</p>
						<div className="flex flex-wrap gap-3">
							{featuredBusinesses.map((business) => (
								<Link
									key={business.id}
									to={`/business/${business.slug}`}
									className="group"
								>
									<Badge
										variant="outline"
										className="hover:bg-accent hover:text-accent-foreground cursor-pointer px-4 py-2 transition-colors"
									>
										{business.name}
										{business.categories[0] && (
											<span className="text-muted-foreground ml-2">
												({business.categories[0].name})
											</span>
										)}
									</Badge>
								</Link>
							))}
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    OTHER CITIES
			    ═══════════════════════════════════════════════════════════════════ */}
			{otherCities.length > 0 && (
				<section className="border-t py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						<div>
							<h2 className="mb-6 text-xl font-medium">Other DFW cities</h2>
							<div className="flex flex-wrap gap-2">
								{otherCities.map((otherCity) => (
									<Link key={otherCity.id} to={`/cities/${otherCity.slug}`}>
										<Badge
											variant="secondary"
											className="hover:bg-accent hover:text-accent-foreground cursor-pointer px-3 py-1.5 transition-colors"
										>
											{otherCity.name}
										</Badge>
									</Link>
								))}
								<Link to="/cities">
									<Badge
										variant="outline"
										className="cursor-pointer px-3 py-1.5"
									>
										View all cities →
									</Badge>
								</Link>
							</div>
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    FINAL CTA
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="bg-muted/30 border-t py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className={PAGE_PROSE}>
						<h2 className="mb-4 font-serif text-xl tracking-tight">
							Missing a business that serves {city.name}?
						</h2>
						{/*
						 * Not "submit a recommendation". A submission adds a business to
						 * the catalog to be scored like any other; it is not a vote and
						 * it does not affect rank. Calling it a recommendation implied
						 * reader nominations feed the rankings, which would make the
						 * rubric look negotiable.
						 */}
						<p className="text-muted-foreground mb-6">
							Submit it and it will be scored against the same rubric as
							everything else here. Submissions do not affect rank.
						</p>
						<Button variant="outline" asChild>
							<Link to="/submit">
								<Icon name="plus" className="mr-2 size-4" />
								Submit a business
							</Link>
						</Button>
					</div>
				</div>
			</section>
		</>
	)
}
